import React, { memo, useMemo } from 'react';
import { getCountries } from '@honeybook/i18n';
import { useLocale } from '@honeybook/i18n-react';
import HBDropdownField from '../HBDropdownField/HBDropdownField';
import type { HBDropdownFieldProps } from '../HBDropdownField/HBDropdownField';
import { useHBTranslation } from '../HBUIProvider/HBUIProvider';
import { SearchableDropdownWrapper } from './HBAddress.styles';

export type CountryOption = { value: string; label: string };

export type HBAddressCountryFieldProps = Omit<
    HBDropdownFieldProps<string>,
    'options' | 'label'
> & {
    /** Field label. Defaults to "Country". */
    label?: string;
    /**
     * Override the auto-generated country list.
     * Defaults to all ISO 3166-1 countries from @honeybook/i18n getCountries(),
     * localized to the current locale.
     */
    countryOptions?: CountryOption[];
    /** Identifier segment appended to the component name to build data-testid. */
    testIdName?: string;
    /** Explicit test ID string; sets data-testid directly. */
    testId?: string;
};

const HBAddressCountryField = ({
    label,
    countryOptions,
    ...props
}: HBAddressCountryFieldProps) => {
    const t = useHBTranslation();
    const { locale } = useLocale();
    const defaultOptions = useMemo(
        () => getCountries(locale).map(c => ({ value: c.code, label: c.name })),
        [locale]
    );

    return (
        <SearchableDropdownWrapper>
            <HBDropdownField
                placeholder={t('hbAddress.selectCountry')}
                {...props}
                label={label ?? t('hbAddress.country')}
                options={countryOptions ?? defaultOptions}
                isSearchable
            />
        </SearchableDropdownWrapper>
    );
};

export default memo(HBAddressCountryField);
